export type Capability = {
  id: string;
  title: string;
  description: string;
  group:
    | "Model"
    | "Agent"
    | "Application"
    | "Infrastructure"
    | "Edge"
    | "Hardware"
    | "Productization";
  code: string;
};

export const capabilities: Capability[] = [
  {
    id: "llm",
    title: "LLM / VLM / 多模态应用",
    description: "让语言、图像、语音与结构化数据进入同一业务上下文。",
    group: "Model",
    code: "01",
  },
  {
    id: "agents",
    title: "AI Agent / Multi-Agent",
    description: "面向复杂任务的规划、协同、执行与状态管理。",
    group: "Agent",
    code: "02",
  },
  {
    id: "harness",
    title: "Agent Harness",
    description: "构建可靠的工具注册、权限、记忆、追踪与恢复机制。",
    group: "Agent",
    code: "03",
  },
  {
    id: "rag",
    title: "RAG / 企业知识库",
    description: "把分散知识转化为可检索、可引用、可治理的模型上下文。",
    group: "Application",
    code: "04",
  },
  {
    id: "workflow",
    title: "AI Workflow",
    description: "将判断、生成与执行嵌入可观测的业务自动化流程。",
    group: "Application",
    code: "05",
  },
  {
    id: "context",
    title: "Prompt / Context Engineering",
    description: "系统设计指令、上下文窗口与长期记忆。",
    group: "Model",
    code: "06",
  },
  {
    id: "routing",
    title: "Model API 与多模型路由",
    description: "按质量、成本、延迟与可用性动态选择模型。",
    group: "Infrastructure",
    code: "07",
  },
  {
    id: "private",
    title: "开源模型私有化部署",
    description: "满足数据边界、模型主权与专有环境要求。",
    group: "Infrastructure",
    code: "08",
  },
  {
    id: "inference",
    title: "大模型推理服务",
    description: "围绕吞吐、延迟、弹性与可观测性构建推理基础设施。",
    group: "Infrastructure",
    code: "09",
  },
  {
    id: "finetune",
    title: "LoRA / SFT 模型微调",
    description: "用高质量领域数据让模型获得稳定的专用能力。",
    group: "Model",
    code: "10",
  },
  {
    id: "eval",
    title: "模型评测与 Benchmark",
    description: "以离线评测、在线反馈和红队测试度量真实效果。",
    group: "Model",
    code: "11",
  },
  {
    id: "mcp",
    title: "MCP / Tools / Skills",
    description: "用标准协议连接数据、软件服务与执行环境。",
    group: "Agent",
    code: "12",
  },
  {
    id: "coding",
    title: "AI Coding 与开发者工具",
    description: "让 AI 进入研发流程，从代码理解到交付闭环。",
    group: "Application",
    code: "13",
  },
  {
    id: "vision",
    title: "语音 / 视觉 / 多模态",
    description: "理解真实世界的声音、画面、文档与空间信息。",
    group: "Application",
    code: "14",
  },
  {
    id: "edge",
    title: "Edge AI / 端侧 AI",
    description: "在资源受限环境中完成低延迟、隐私友好的智能推理。",
    group: "Edge",
    code: "15",
  },
  {
    id: "iot",
    title: "IoT / 设备连接与控制",
    description: "连接传感器、执行器、控制器与云端业务系统。",
    group: "Edge",
    code: "16",
  },
  {
    id: "hardware-architecture",
    title: "硬件方案与器件选型",
    description: "围绕性能、功耗、成本与供应链完成主控、传感器及外围方案设计。",
    group: "Hardware",
    code: "17",
  },
  {
    id: "pcb",
    title: "原理图与 PCB 设计",
    description: "覆盖原理图、Layout、高速信号、电源完整性与可制造性设计。",
    group: "Hardware",
    code: "18",
  },
  {
    id: "firmware",
    title: "嵌入式固件与驱动",
    description: "开发 BSP、设备驱动、通信协议、实时控制与安全升级能力。",
    group: "Hardware",
    code: "19",
  },
  {
    id: "mechanical",
    title: "工业设计与结构设计",
    description: "统筹人机交互、散热、防护、装配与产品外观的一体化设计。",
    group: "Hardware",
    code: "20",
  },
  {
    id: "prototype",
    title: "样机打样与系统联调",
    description: "完成 EVT 样机、板级调试、软硬件联调与可靠性问题闭环。",
    group: "Productization",
    code: "21",
  },
  {
    id: "production",
    title: "测试认证与量产支持",
    description: "覆盖 DFM、测试工装、认证整改、供应链协同与量产导入。",
    group: "Productization",
    code: "22",
  },
];

export type Project = {
  slug: string;
  ownership: "client" | "self";
  number: string;
  title: string;
  client: string;
  industry: string;
  projectType: string;
  delivery: string;
  status: string;
  summary: string;
  tags: string[];
  challengeTitle: string;
  challenge: string;
  approachTitle: string;
  approach: string;
  systemTitle: string;
  flow: string[];
  proof: [string, string, string];
  result: string;
  resultNote: string;
  accent: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
    label: string;
    kind: "concept" | "live";
  };
  liveUrl?: string;
};

export const projects: Project[] = [
  {
    slug: "pailezu-camera-locker",
    ownership: "client",
    number: "W—01",
    title: "拍乐租智能相机租赁柜",
    client: "常州镜享科技有限公司",
    industry: "智能硬件 · 共享租赁",
    projectType: "IoT 智能硬件与租赁系统",
    delivery: "主板对接 · 后端 · 微信小程序",
    status: "客户项目 · 已交付",
    summary:
      "连接微信小程序、租赁业务后台与 IoT 柜体，实现相机从扫码租赁、开柜取机到归还结算的完整闭环。",
    tags: ["微信小程序", "IoT 智能柜", "设备控制", "租赁系统"],
    challengeTitle: "高价值设备租赁，需要线上与线下始终一致。",
    challenge:
      "相机租赁同时涉及实名认证、支付押金、租赁计时、柜门控制和设备归还。任何一个环节状态不同步，都会影响用户体验和设备安全。",
    approachTitle: "让每一笔线上订单，准确驱动物理柜门。",
    approach:
      "AccSci 对接柜体硬件主板与后端设备服务，开发租赁业务后台和微信小程序，将认证、支付、押金、计费、开柜、归还与异常处理纳入同一状态闭环。",
    systemTitle: "扫码 → 租赁 → 开柜 → 归还",
    flow: ["扫码认证", "支付与押金", "IoT 开柜", "租赁计时", "设备归还"],
    proof: [
      "无人值守租赁跨越线上交易与线下设备",
      "微信小程序 + 业务后端 + IoT 主板",
      "完成从扫码取机到归还结算的业务闭环",
    ],
    result: "让线上订单驱动设备",
    resultNote:
      "项目完成了微信小程序、租赁后端与 IoT 柜体主板的系统连接，使用户可以自助完成认证、支付、取机、计费和归还。",
    accent: "blue",
    image: {
      src: "/images/work/pailezu-concept.webp",
      alt: "拍乐租智能相机租赁柜工业设计方案示意",
      width: 1586,
      height: 992,
      label: "产品方案示意",
      kind: "concept",
    },
  },
  {
    slug: "vibehub",
    ownership: "self",
    number: "P—01",
    title: "VibeHub AI 创作与应用平台",
    client: "AccSci",
    industry: "AI 平台 · 创作者生态",
    projectType: "AI 原生内容与应用平台",
    delivery: "产品设计 · 全栈开发 · 平台运营",
    status: "自研产品 · 在线运行",
    summary:
      "为 AI 创作者提供作品发布、浏览器即时运行、探索推荐、排行榜与开发者中心的一体化平台。",
    tags: ["AI Platform", "Browser Runtime", "Creator Tools"],
    challengeTitle: "AI 作品很多，真正的使用入口很少。",
    challenge:
      "游戏、互动故事和实用工具分散在不同链接和发布渠道中，创作者难以获得持续曝光，也缺少来自真实试玩的反馈。",
    approachTitle: "把作品发布、即时运行与真实反馈放在同一平台。",
    approach:
      "AccSci 构建作品上传、内容广场、浏览器运行环境、探索机制、排行榜和开发者中心，让创作者可以持续发布迭代，用户可以点开即用。",
    systemTitle: "发布 → 运行 → 探索 → 反馈",
    flow: ["作品发布", "内容入库", "浏览器运行", "探索推荐", "反馈排行"],
    proof: [
      "AI 作品缺少统一发布、运行与反馈渠道",
      "创作者工具 + 浏览器运行时 + 探索排行",
      "作品从上传进入真实试玩和持续迭代",
    ],
    result: "从作品发布，到真实用户反馈",
    resultNote:
      "平台已形成包含作品广场、即时试玩、排行榜和开发者中心的完整产品结构，并持续承载创作者发布的新作品。",
    accent: "cyan",
    image: {
      src: "/images/work/vibehub-site.webp",
      alt: "VibeHub AI 创作与应用平台上线页面",
      width: 1440,
      height: 900,
      label: "已上线项目界面",
      kind: "live",
    },
    liveUrl: "https://gamesvibe.app/",
  },
  {
    slug: "jingbo-printing",
    ownership: "client",
    number: "W—03",
    title: "晶博工业印刷数字门户",
    client: "苏州晶博印刷有限公司",
    industry: "工业企业 · 数字门户",
    projectType: "工业 B2B 企业官网",
    delivery: "内容架构 · UI 设计 · 前端开发",
    status: "客户项目 · 已上线",
    summary:
      "围绕工业印刷耗材、色彩管理与技术服务，建立清晰的产品信息和询价转化入口。",
    tags: ["B2B Website", "Content Design", "Lead Conversion"],
    challengeTitle: "专业能力很深，但客户在线上难以快速看懂。",
    challenge:
      "油墨、橡皮布、清洗布和免冲洗版等产品信息分散，色彩管理与技术服务的价值也依赖销售人员反复解释。",
    approachTitle: "让产品与技术内容形成清晰的客户路径。",
    approach:
      "AccSci 完成网站内容架构、视觉设计和前端开发，将核心产品、色彩管理、企业能力与询价入口组织为清晰的 B2B 数字门户。",
    systemTitle: "理解 → 选型 → 服务 → 询价",
    flow: ["需求梳理", "产品分类", "内容设计", "前端开发", "询价转化"],
    proof: [
      "产品与技术服务依赖线下反复说明",
      "产品信息架构 + 品牌视觉 + 询价入口",
      "让工业能力在线清晰呈现并支持转化",
    ],
    result: "让工业能力，在线清晰呈现",
    resultNote:
      "新网站把产品分类、色彩管理能力和技术服务放进统一的客户理解路径，并提供面向业务咨询的清晰入口。",
    accent: "graphite",
    image: {
      src: "/images/work/jingbo-site.webp",
      alt: "晶博工业印刷数字门户上线页面",
      width: 1440,
      height: 900,
      label: "已上线项目界面",
      kind: "live",
    },
    liveUrl: "https://ink.lumigrav.space/",
  },
  {
    slug: "liangdian-design",
    ownership: "client",
    number: "W—04",
    title: "亮典设计制作业务官网",
    client: "亮典广告设计",
    industry: "创意服务 · 数字体验",
    projectType: "服务型企业数字官网",
    delivery: "业务梳理 · UI 设计 · 前端开发",
    status: "客户项目 · 已上线",
    summary:
      "把平面设计、印刷包装、广告标识与视频制作组织成可理解、可验证、可直接咨询的线上业务体验。",
    tags: ["Digital Experience", "Case Gallery", "Conversion"],
    challengeTitle: "服务品类很多，但客户看不到从创意到交付的整体能力。",
    challenge:
      "设计、制作、印刷和现场安装分布在不同业务描述中，真实案例和生产能力缺少统一展示，客户难以快速判断匹配度。",
    approachTitle: "从真实业务资料出发，把服务和交付过程讲清楚。",
    approach:
      "AccSci 梳理服务结构、精选制作案例和生产流程，完成网站视觉与前端开发，让客户能够理解能力、查看作品并快速发起咨询。",
    systemTitle: "需求 → 设计 → 制作 → 交付",
    flow: ["业务梳理", "内容策划", "案例组织", "前端开发", "咨询转化"],
    proof: [
      "多类设计制作服务缺少统一线上表达",
      "业务梳理 + 案例画廊 + 转化路径",
      "从服务理解自然进入项目咨询",
    ],
    result: "让创意服务，回到真实交付",
    resultNote:
      "网站以实际服务和制作案例为核心，建立从了解能力、查看作品到直接咨询的完整访问路径。",
    accent: "amber",
    image: {
      src: "/images/work/liangdian-site.webp",
      alt: "亮典设计制作业务官网上线页面",
      width: 1440,
      height: 900,
      label: "已上线项目界面",
      kind: "live",
    },
    liveUrl: "https://media.lumigrav.space/",
  },
];

export const clientProjects = projects.filter(
  (project) => project.ownership === "client",
);

export const selfDevelopedProjects = projects.filter(
  (project) => project.ownership === "self",
);

export const products = [
  {
    id: "orchestrator",
    status: "产品概念 · 示例",
    name: "AccSci Orchestrator",
    tagline: "让 Agent 系统可编排、可观测、可治理。",
    description: "统一管理模型、工具、上下文与执行轨迹的 Agent 运行底座。",
    features: ["多模型路由", "工具权限与沙箱", "全链路追踪"],
    image: {
      src: "/images/products/orchestrator.webp",
      alt: "AccSci Orchestrator 多模块智能编排核心概念图",
      width: 1000,
      height: 1000,
    },
  },
  {
    id: "edge-hub",
    status: "产品概念 · 示例",
    name: "AccSci Edge Hub",
    tagline: "面向现场智能的边缘计算硬件。",
    description: "集成算力、工业接口与安全连接能力的可定制 AI 边缘网关方案。",
    features: ["多规格 AI 算力", "工业协议与接口", "整机与 PCB 定制"],
    image: {
      src: "/images/products/edge-hub.webp",
      alt: "AccSci Edge Hub 工业边缘计算网关概念图",
      width: 1000,
      height: 1000,
    },
  },
  {
    id: "device-kit",
    status: "产品概念 · 示例",
    name: "AccSci Device Kit",
    tagline: "从原型验证到智能硬件产品。",
    description:
      "包含参考硬件、嵌入式固件、Edge Runtime 与云端设备服务的产品化底座。",
    features: ["参考设计与固件", "端侧模型运行", "量产与设备管理"],
    image: {
      src: "/images/products/device-kit.webp",
      alt: "AccSci Device Kit 智能设备开发套件爆炸视图概念图",
      width: 1000,
      height: 1000,
    },
  },
];

export const navItems = [
  { label: "服务", to: "/capabilities" },
  { label: "客户案例", to: "/work" },
  { label: "产品", to: "/products" },
  { label: "关于", to: "/about" },
] as const;

export const processSteps = [
  {
    index: "01",
    title: "系统定义",
    text: "同时明确业务目标、智能能力、设备形态、成本与量产约束。",
  },
  {
    index: "02",
    title: "联合原型",
    text: "用 AI 原型、开发板与结构样件验证关键体验和工程边界。",
  },
  {
    index: "03",
    title: "软硬件开发",
    text: "并行推进应用、云服务、端侧模型、PCB、固件与结构设计。",
  },
  {
    index: "04",
    title: "验证与产品化",
    text: "完成系统联调、测试认证、样机迭代与量产导入支持。",
  },
];
